import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { X, ArrowLeft, Check, Minus } from "lucide-react";

const COMPARISON_METRICS = [
  { key: "match_score", label: "Match Score", format: "percent" },
  { key: "cost_of_living", label: "Cost of Living", format: "rating" },
  { key: "safety", label: "Safety Index", format: "rating" },
  { key: "healthcare", label: "Healthcare Quality", format: "rating" },
  { key: "english_friendly", label: "English Friendly", format: "boolean" },
  { key: "visa_ease", label: "Visa Accessibility", format: "rating" },
  { key: "climate", label: "Climate Type", format: "text" },
  { key: "internet_quality", label: "Internet Quality", format: "rating" },
  { key: "expat_community", label: "Expat Community", format: "rating" },
  { key: "travel_connectivity", label: "Travel Connectivity", format: "rating" }
];

// Generate comparison data based on country recommendations
const generateComparisonData = (country) => {
  // Seed random based on country name for consistency
  const seed = country.country_name.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
  const random = (min, max) => {
    const x = Math.sin(seed + min * max) * 10000;
    return Math.floor((x - Math.floor(x)) * (max - min + 1)) + min;
  };

  const climates = {
    "Portugal": "Mediterranean",
    "Spain": "Mediterranean", 
    "Costa Rica": "Tropical",
    "Mexico": "Varied",
    "Thailand": "Tropical",
    "Panama": "Tropical",
    "Ecuador": "Varied",
    "Malaysia": "Tropical",
    "Colombia": "Varied",
    "Vietnam": "Tropical",
    "France": "Temperate",
    "Germany": "Temperate",
    "Canada": "Cold",
    "Netherlands": "Temperate",
    "New Zealand": "Temperate",
    "Australia": "Varied",
    "Japan": "Temperate",
    "Singapore": "Tropical",
    "Italy": "Mediterranean",
    "Greece": "Mediterranean"
  };

  const englishFriendly = ["Portugal", "Netherlands", "Germany", "Singapore", "Malaysia", "Thailand", "Philippines", "Canada", "Australia", "New Zealand", "Ireland"];

  return {
    country_name: country.country_name,
    image_url: country.image_url,
    match_score: country.match_score,
    cost_of_living: random(50, 95),
    safety: random(60, 95),
    healthcare: random(55, 95),
    english_friendly: englishFriendly.includes(country.country_name),
    visa_ease: random(40, 90),
    climate: climates[country.country_name] || "Varied",
    internet_quality: random(60, 95),
    expat_community: random(50, 95),
    travel_connectivity: random(55, 95)
  };
};

const ComparisonPage = () => {
  const navigate = useNavigate();
  const [selectedCountries, setSelectedCountries] = useState([]);
  
  // Get recommendations from session storage
  const stored = sessionStorage.getItem("expatmatch_recommendations");
  const recommendations = stored ? JSON.parse(stored) : [];
  
  // Generate comparison data for all countries
  const comparisonData = recommendations.map(generateComparisonData);

  const toggleCountry = (country) => {
    if (selectedCountries.find(c => c.country_name === country.country_name)) {
      setSelectedCountries(selectedCountries.filter(c => c.country_name !== country.country_name));
    } else if (selectedCountries.length < 4) {
      setSelectedCountries([...selectedCountries, country]);
    }
  };

  const renderValue = (metric, value) => {
    switch (metric.format) {
      case "percent":
        return (
          <span className="font-mono text-lg font-bold text-primary">
            {value}%
          </span>
        );
      case "rating":
        const color = value >= 80 ? "text-accent" : value >= 60 ? "text-secondary" : "text-muted-foreground";
        return (
          <div className="flex items-center gap-2">
            <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${value >= 80 ? "bg-accent" : value >= 60 ? "bg-secondary" : "bg-muted-foreground"}`}
                style={{ width: `${value}%` }}
              />
            </div>
            <span className={`font-mono text-sm ${color}`}>{value}</span>
          </div>
        );
      case "boolean":
        return value ? (
          <Check className="h-5 w-5 text-accent" />
        ) : (
          <Minus className="h-5 w-5 text-muted-foreground" />
        );
      case "text":
        return <span className="text-foreground">{value}</span>;
      default:
        return value;
    }
  };

  if (recommendations.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-primary mb-4">No Countries to Compare</h2>
          <p className="text-muted-foreground mb-6">
            Complete the questionnaire first to get country recommendations.
          </p>
          <Button onClick={() => navigate("/questionnaire")} data-testid="start-questionnaire-btn">
            Start Questionnaire
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/results")}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Results
          </button>
          <h1 className="text-3xl sm:text-4xl font-bold text-primary mb-2" data-testid="comparison-title">
            Compare Countries
          </h1>
          <p className="text-muted-foreground">
            Select up to 4 countries to compare side-by-side
          </p>
        </div>

        {/* Country Selection */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-primary mb-4">Select Countries to Compare</h2>
          <div className="flex flex-wrap gap-3">
            {comparisonData.map((country) => {
              const isSelected = selectedCountries.find(c => c.country_name === country.country_name);
              return (
                <button
                  key={country.country_name}
                  onClick={() => toggleCountry(country)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                    isSelected 
                      ? "bg-primary text-white border-primary" 
                      : "bg-card border-primary/20 hover:border-primary/50"
                  } ${selectedCountries.length >= 4 && !isSelected ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={selectedCountries.length >= 4 && !isSelected}
                  data-testid={`select-${country.country_name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {isSelected && <Check className="h-4 w-4" />}
                  <span>{country.country_name}</span>
                  <span className="font-mono text-sm opacity-70">{country.match_score}%</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Comparison Table */}
        {selectedCountries.length > 0 ? (
          <div className="bg-card rounded-2xl border border-primary/10 overflow-hidden">
            {/* Table Header with Country Images */}
            <div className="grid" style={{ gridTemplateColumns: `200px repeat(${selectedCountries.length}, 1fr)` }}>
              <div className="p-4 bg-primary/5 border-b border-r border-primary/10">
                <span className="font-semibold text-primary">Metrics</span>
              </div>
              {selectedCountries.map((country) => (
                <div key={country.country_name} className="border-b border-primary/10 relative">
                  <button
                    onClick={() => toggleCountry(country)}
                    className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors"
                    data-testid={`remove-${country.country_name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                  <div className="h-32 relative overflow-hidden">
                    <img 
                      src={country.image_url} 
                      alt={country.country_name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-3 left-4">
                      <h3 className="text-white font-bold text-lg">{country.country_name}</h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Metrics Rows */}
            {COMPARISON_METRICS.map((metric, index) => (
              <div 
                key={metric.key}
                className={`grid ${index % 2 === 0 ? "bg-background" : "bg-card"}`}
                style={{ gridTemplateColumns: `200px repeat(${selectedCountries.length}, 1fr)` }}
              >
                <div className="p-4 border-r border-primary/10 flex items-center">
                  <span className="text-sm font-medium text-foreground">{metric.label}</span>
                </div>
                {selectedCountries.map((country) => (
                  <div 
                    key={`${country.country_name}-${metric.key}`}
                    className="p-4 flex items-center justify-center border-r border-primary/5 last:border-r-0"
                  >
                    {renderValue(metric, country[metric.key])}
                  </div>
                ))}
              </div>
            ))}

            {/* Action Row */}
            <div 
              className="grid bg-primary/5"
              style={{ gridTemplateColumns: `200px repeat(${selectedCountries.length}, 1fr)` }}
            >
              <div className="p-4 border-r border-primary/10">
                <span className="text-sm font-medium text-primary">Get Full Guide</span>
              </div>
              {selectedCountries.map((country) => (
                <div 
                  key={`${country.country_name}-action`}
                  className="p-4 flex items-center justify-center"
                >
                  <Button
                    size="sm"
                    onClick={() => navigate("/results")}
                    className="btn-animated bg-secondary hover:bg-secondary/90"
                    data-testid={`buy-${country.country_name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    $20 Guide
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-card rounded-2xl border border-primary/10 p-12 text-center">
            <p className="text-muted-foreground">
              Select at least one country above to start comparing
            </p>
          </div>
        )}

        {/* Bundle Pricing Banner */}
        <div className="mt-8 bg-gradient-to-r from-secondary/10 to-accent/10 rounded-xl border border-secondary/20 p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-primary text-lg">Ready to dive deeper?</h3>
              <p className="text-sm text-muted-foreground">
                Get detailed guides for your top picks: <strong>$20</strong> for 1 | <strong>$30</strong> for 2 | <strong>$35</strong> for 3
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-accent/20 text-accent text-xs px-2 py-1 rounded-full font-medium">Save up to $25</span>
              <Button
                onClick={() => navigate("/results")}
                className="btn-animated bg-secondary hover:bg-secondary/90"
              >
                View Bundle Options
              </Button>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-8 bg-card rounded-xl border border-primary/10 p-6">
          <h3 className="font-semibold text-primary mb-4">Understanding the Metrics</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-foreground">Cost of Living:</span>
              <span className="text-muted-foreground ml-2">Higher = more affordable</span>
            </div>
            <div>
              <span className="font-medium text-foreground">Safety Index:</span>
              <span className="text-muted-foreground ml-2">Based on crime rates</span>
            </div>
            <div>
              <span className="font-medium text-foreground">Healthcare:</span>
              <span className="text-muted-foreground ml-2">Quality & accessibility</span>
            </div>
            <div>
              <span className="font-medium text-foreground">Visa Accessibility:</span>
              <span className="text-muted-foreground ml-2">Ease for US citizens</span>
            </div>
            <div>
              <span className="font-medium text-foreground">Expat Community:</span>
              <span className="text-muted-foreground ml-2">Size & activity</span>
            </div>
            <div>
              <span className="font-medium text-foreground">Travel Connectivity:</span>
              <span className="text-muted-foreground ml-2">Flights to/from US</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonPage;
